/*  Batch-conductor sizing · CSV handlers.
    Pure helpers extracted from BatchConductor.jsx during the v2.87 refactor.
    Keep all filesystem/DOM interactions here so the main component stays
    focused on state + rendering.
*/
import { CSV_COLUMNS, CSV_NUMERIC, CSV_BOOL, parseBool, parseCSV, blankRow } from "./constants";

/** Build the sample CSV template (header + 3 example rows covering
 *  feeder / motor-branch / transformer modes). Returns a Blob. */
export function buildTemplateCsvBlob() {
  const header = CSV_COLUMNS.join(",");
  const example = [
    "C-1,feeder,80,A,1.25,480,3,100,75,30,1,THWN-2,copper,conduit,,false,,,,,,,",
    "MTR-1,motor_branch,25,hp,1.25,480,3,150,75,30,1,THWN-2,copper,conduit,EMT,false,inverse_time_breaker,1.15,,,,,",
    "TX-1,transformer,,A,1.0,480,3,50,75,30,1,THWN-2,copper,conduit,RMC,false,,,75,208,primary,false,",
  ].join("\n");
  return new Blob([header + "\n" + example + "\n"], { type: "text/csv" });
}

/** Parse a CSV blob body into batch rows.
 *  Returns `{ ok, rows?, unknownColumns?, error? }` — pure, no toast. */
export function parseCsvToRows(text) {
  const parsed = parseCSV(text);
  if (parsed.length < 2) {
    return { ok: false, error: "CSV needs a header row + at least one data row." };
  }
  const headers = parsed[0].map((h) => h.trim());
  const unknownColumns = headers.filter((h) => h && !CSV_COLUMNS.includes(h));
  const dataLines = parsed.slice(1).filter((r) => r.some((c) => (c ?? "").trim() !== ""));
  if (dataLines.length === 0) {
    return { ok: false, error: "No data rows found in the CSV." };
  }
  const rows = dataLines.map((cells, idx) => {
    const base = blankRow(idx + 1);
    headers.forEach((h, hi) => {
      if (!CSV_COLUMNS.includes(h)) return;
      const raw = (cells[hi] ?? "").trim();
      if (raw === "") return;
      if (CSV_NUMERIC.has(h)) base[h] = Number(raw);
      else if (CSV_BOOL.has(h)) base[h] = parseBool(raw);
      else base[h] = raw;
    });
    if (base.load_unit === "A") base.load_amps = Number(base.load_value) || 0;
    return base;
  });
  return { ok: true, rows, unknownColumns };
}

/** Build the results CSV blob — 48 columns of inputs + status +
 *  full sized outputs + mode-specific fields + error. */
export function buildResultsCsvBlob(rows, results) {
  const headers = [
    "tag", "circuit_type",
    "load_value", "load_unit", "load_amps_effective", "continuous_factor",
    "voltage", "phases", "length_ft", "temp_rating", "ambient_c",
    "parallel_sets", "cable_type", "conductor_material", "media",
    "conduit_material", "allow_next_size_up",
    "compute_status",
    "selected_size", "selection_driven_by",
    "table_ampacity", "derated_ampacity", "total_derated_ampacity",
    "required_ampacity", "recommended_breaker",
    "voltage_drop_pct", "voltage_drop_volts", "voltage_drop_compliant",
    "voltage_drop_model", "ambient_factor", "media_factor", "combined_factor",
    "ocpd_exception_used",
    "conduit_trade_size", "conduit_fill_pct", "conduit_raceways_required",
    "conduit_egc_size",
    "motor_branch_fla", "motor_branch_ocpd_amps", "motor_branch_overload_setpoint_amps",
    "transformer_primary_fla", "transformer_primary_ocpd_amps",
    "transformer_secondary_fla", "transformer_secondary_ocpd_amps",
    "motor_group_feeder_ampacity_min", "motor_group_feeder_ocpd",
    "motor_group_largest_motor_fla",
    "error",
  ];
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(",")];
  rows.forEach((r, i) => {
    const wrap = results[i];
    const res = wrap?.ok ? (wrap.result || {}) : null;
    const mb = res?.motor_branch;
    const tx = res?.transformer;
    const mg = res?.motor_group?.supported ? res.motor_group : null;
    const cn = res?.conduit?.supported ? res.conduit : null;
    const cells = [
      r.tag, r.circuit_type,
      r.load_value, r.load_unit, res?.input?.load_amps ?? "", r.continuous_factor,
      r.voltage, r.phases, r.length_ft, r.temp_rating, r.ambient_c,
      r.parallel_sets, r.cable_type, r.conductor_material, r.media,
      r.conduit_material || "", r.allow_next_size_up ? "true" : "false",
      wrap ? (wrap.ok ? "ok" : "error") : "not_computed",
      res?.selected_size ?? "", res?.selection_driven_by ?? "",
      res?.table_ampacity ?? "", res?.derated_ampacity ?? "",
      res?.total_derated_ampacity ?? "", res?.required_ampacity ?? "",
      res?.recommended_breaker ?? "",
      res?.voltage_drop_pct ?? "", res?.voltage_drop_volts ?? "",
      res?.voltage_drop_compliant ?? "",
      res?.voltage_drop_model ?? "", res?.ambient_factor ?? "",
      res?.media_factor ?? "", res?.combined_factor ?? "",
      res?.ocpd_exception_used ? "true" : "",
      cn?.trade_size ?? "", cn?.fill_pct ?? "", cn?.raceways_required ?? "",
      cn?.per_raceway?.egc_size ?? "",
      mb?.fla ?? "", mb?.recommended_ocpd_amps ?? "",
      mb?.overload?.setpoint_amps ?? "",
      tx?.primary?.fla ?? "", tx?.primary?.recommended_ocpd_amps ?? "",
      tx?.secondary?.fla ?? "", tx?.secondary?.recommended_ocpd_amps ?? "",
      mg?.feeder_ampacity_min ?? "", mg?.feeder_ocpd ?? "",
      mg?.largest_motor_fla ?? "",
      wrap?.ok === false ? wrap.error : "",
    ];
    lines.push(cells.map(escape).join(","));
  });
  return new Blob([lines.join("\n") + "\n"], { type: "text/csv" });
}

/** Utility — trigger a blob download with a given filename. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
